// ============================================================================
// MarketHomePage — главная витрины: крупная кнопка «Оставить заявку»,
// категории-плитки, последние заявки и краткое объяснение сценария.
// Мобильный экран: одна колонка, без таблиц и окон платформы.
//
// ГОСТЬ: кнопки «Оставить заявку»/категории НЕ открывают мастер заявки —
// вызывают onRequireAuth (вход/регистрация). Авторизованный клиент видит
// мастер заявки; мастер-исполнитель сюда не попадает (у него свой кабинет).
//
// ЧЕРНОВИК: если заявка не была отправлена, показывается карточка
// «Черновик заявки» с продолжением и удалением (явное состояние).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Briefcase, ClipboardList, FileText, Loader2, Plus, RefreshCw, ShieldCheck, Sparkles, Trash2, UserRound, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OfflineDataBanner } from '@/components/ui/OfflineDataBanner';
import { useAuthStore } from '@/store/authStore';
import { logErrorToJournal } from '@/lib/errors';
import {
  isMarketPhoneBlocked,
  loadMarketCategories,
  loadMyMarketRequests,
  toMarketFailure,
  type MarketCategory,
  type MarketFailure,
  type MarketRequest,
} from '@/lib/market';
import { MarketError, MarketLoading } from '@/components/market/MarketState';
import { MarketPhoneStatus } from '@/components/market/MarketPhoneStatus';
import { MarketRequestCard } from '@/components/market/MarketRequestCard';
import { MarketTopBar } from '@/components/market/MarketTopBar';
import { marketDraftSummary, type MarketDraft } from '@/components/market/marketDraft';
import { useI18n } from '@/lib/i18n/I18nProvider';

interface MarketHomePageProps {
  authenticated: boolean;
  onRequireAuth: () => void;
  draft: MarketDraft | null;
  onResumeDraft: () => void;
  onDiscardDraft: () => void;
  onStart: (categoryId?: string) => void;
  onOpenRequests: () => void;
  onOpenRequest: (requestId: string) => void;
  onOpenMasterFeed: () => void;
  onOpenWallet: () => void;
  /** Профиль клиента: контакты, адреса, уведомления. */
  onOpenProfile: () => void;
  /** Кабинет оператора — только для роли «Оператор маркетплейса»/админа. */
  onOpenOperator?: () => void;
}

export function MarketHomePage({
  authenticated,
  onRequireAuth,
  draft,
  onResumeDraft,
  onDiscardDraft,
  onStart,
  onOpenRequests,
  onOpenRequest,
  onOpenMasterFeed,
  onOpenWallet,
  onOpenProfile,
  onOpenOperator,
}: MarketHomePageProps) {
  const userId = String(useAuthStore((s) => s.user?.id || ''));
  const { t, locale } = useI18n();
  const [categories, setCategories] = useState<MarketCategory[] | null>(null);
  const [recent, setRecent] = useState<MarketRequest[] | null>(null);
  const [categoryFailure, setCategoryFailure] = useState<MarketFailure | null>(null);
  const [requestFailure, setRequestFailure] = useState<MarketFailure | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setCategoryFailure(null);
    setRequestFailure(null);
    try {
      const list = await loadMarketCategories();
      setCategories(list);
    } catch (e) {
      logErrorToJournal(e, 'Витрина маркетплейса: категории на главной');
      setCategoryFailure(toMarketFailure(e));
    }
    // «Мои заявки» — только для вошедшего пользователя (гость получает
    // экран входа, а не ошибку «не определён пользователь»).
    if (!authenticated || !userId) {
      setRecent([]);
      setLoading(false);
      return;
    }
    try {
      const list = await loadMyMarketRequests(userId);
      setRecent(list.slice(0, 3));
    } catch (e) {
      logErrorToJournal(e, 'Витрина маркетплейса: мои заявки на главной');
      setRequestFailure(toMarketFailure(e));
    } finally {
      setLoading(false);
    }
  }, [authenticated, userId]);

  useEffect(() => {
    void load();
  }, [load, tick]);

  const selectable = (categories || []).filter((c) => !c.isGroup);

  const startRequest = (categoryId?: string) => {
    if (!authenticated) {
      onRequireAuth();
      return;
    }
    onStart(categoryId);
  };

  const phoneBlockedRecent = (recent || []).find(
    (request) => request.status === 'new' && isMarketPhoneBlocked(request),
  ) || null;
  const draftCategory = draft?.categoryId
    ? selectable.find((c) => c.id === draft.categoryId)?.name
    : '';
  const draftText = marketDraftSummary(draft, draftCategory);

  return (
    <div className="flex h-full flex-col">
      <MarketTopBar
        title={t('market.title')}
        subtitle={t('market.subtitle')}
        action={(
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 rounded-lg px-2 text-xs"
              onClick={() => (authenticated ? onOpenRequests() : onRequireAuth())}
            >
              {t('market.my_requests')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 rounded-lg p-0"
              onClick={() => (authenticated ? onOpenProfile() : onRequireAuth())}
              aria-label={t('market.profile')}
              title={t('market.profile')}
            >
              <UserRound className="h-4 w-4" />
            </Button>
          </div>
        )}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl space-y-5 p-4 pb-8 sm:p-6">
          <OfflineDataBanner sourceKey={`market:requests:${userId}`} />

          {phoneBlockedRecent && (
            <MarketPhoneStatus
              state="unverified"
              audience="client"
              hint={phoneBlockedRecent.phoneVerificationHint || undefined}
              onOpenProfile={onOpenProfile}
            />
          )}

          {/* Главный вход сценария */}
          <section className="overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/15 via-primary/5 to-background p-5 shadow-sm sm:p-6">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-background/70 px-2.5 py-1 text-[11px] font-medium text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              {t('market.select_master')}
            </div>
            <h1 className="mt-3 text-2xl font-semibold leading-tight sm:text-3xl">
              {t('market.hero_title')}
            </h1>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {t('market.hero_text')}
            </p>

            {/* Черновик — явное состояние: продолжить или удалить */}
            {draft && (
              <div className="mt-4 rounded-2xl border border-primary/25 bg-background/80 p-3.5">
                <div className="flex items-start gap-2">
                  <FileText className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{t('market.draft')}</div>
                    <div className="mt-0.5 break-words text-xs leading-5 text-muted-foreground">
                      {draftText || t('market.draft_saved_device')}
                    </div>
                    {draft.updatedAt > 0 && (
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        {t('market.draft_saved_at', { date: new Date(draft.updatedAt).toLocaleString(locale) })}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={onDiscardDraft}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                    aria-label={t('market.delete_draft')}
                    title={t('market.delete_draft')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="mt-3 h-10 w-full rounded-xl"
                  onClick={() => (authenticated ? onResumeDraft() : onRequireAuth())}
                >
                  {t('market.continue_draft')}
                </Button>
              </div>
            )}

            <Button
              size="lg"
              className="mt-5 h-12 w-full rounded-xl text-base shadow-sm"
              onClick={() => startRequest()}
            >
              <Plus className="mr-2 h-5 w-5" />
              {t('market.leave_request')}
            </Button>
            <Button
              variant="ghost"
              className="mt-1.5 h-10 w-full rounded-xl text-muted-foreground"
              onClick={() => (authenticated ? onOpenRequests() : onRequireAuth())}
            >
              <ClipboardList className="mr-2 h-4 w-4" />
              {t('market.my_requests')}
            </Button>
            {!authenticated && (
              <p className="mt-2 text-center text-[11px] leading-4 text-muted-foreground">
                Заявку можно создать после входа — гость увидит форму входа и регистрацию.
              </p>
            )}
            <Button
              variant="ghost"
              className="mt-1.5 h-10 w-full rounded-xl text-muted-foreground"
              onClick={() => (authenticated ? onOpenProfile() : onRequireAuth())}
            >
              <UserRound className="mr-2 h-4 w-4" />
              {t('market.profile_addresses')}
            </Button>
            <Button
              variant="outline"
              className="mt-1.5 h-11 w-full rounded-xl"
              onClick={onOpenMasterFeed}
            >
              <Briefcase className="mr-2 h-4 w-4" />
              {t('market.feed_for_master')}
            </Button>
            <Button
              variant="ghost"
              className="mt-1.5 h-10 w-full rounded-xl text-muted-foreground"
              onClick={onOpenWallet}
            >
              <Wallet className="mr-2 h-4 w-4" />
              {t('market.wallet')}
            </Button>
            {onOpenOperator && (
              <Button
                variant="outline"
                className="mt-1.5 h-11 w-full rounded-xl"
                onClick={onOpenOperator}
              >
                <ShieldCheck className="mr-2 h-4 w-4" />
                {t('market.operator')}
              </Button>
            )}
          </section>

          {/* Категории — быстрый старт с уже выбранной услугой */}
          <section>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">{t('market.start_here')}</h2>
              <button
                type="button"
                onClick={() => setTick((n) => n + 1)}
                className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                disabled={loading}
              >
                <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
                {t('market.refresh')}
              </button>
            </div>

            {categoryFailure ? (
              <MarketError failure={categoryFailure} onRetry={() => setTick((n) => n + 1)} />
            ) : loading && categories === null ? (
              <MarketLoading text={t('market.categories_loading')} />
            ) : selectable.length === 0 ? (
              <div className="rounded-2xl border border-dashed bg-card/50 px-4 py-5 text-sm text-muted-foreground">
                Категории услуг ещё не настроены. Обратитесь к администратору базы — после
                заполнения справочника выбор появится здесь.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {selectable.slice(0, 9).map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => startRequest(category.id)}
                    className="flex min-h-[64px] items-center justify-between gap-2 rounded-2xl border bg-card px-3.5 py-3 text-left text-sm font-medium transition-colors hover:border-primary/40 active:bg-accent/40"
                  >
                    <span className="line-clamp-2">{category.name}</span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* Последние заявки пользователя */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold">{t('market.recent')}</h2>
              {(recent?.length || 0) > 0 && (
                <button
                  type="button"
                  onClick={onOpenRequests}
                  className="text-[11px] font-medium text-primary"
                >
                  {t('market.all_requests')}
                </button>
              )}
            </div>

            {!authenticated ? (
              <div className="rounded-2xl border border-dashed bg-card/50 px-4 py-5 text-sm text-muted-foreground">
                {t('market.signin_requests')}
              </div>
            ) : requestFailure ? (
              <MarketError failure={requestFailure} onRetry={() => setTick((n) => n + 1)} />
            ) : loading && recent === null ? (
              <div className="flex items-center gap-2 py-5 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t('market.recent_loading')}
              </div>
            ) : (recent?.length || 0) === 0 ? (
              <div className="rounded-2xl border border-dashed bg-card/50 px-4 py-5 text-sm text-muted-foreground">
                {t('market.recent_empty')}
              </div>
            ) : (
              <div className="space-y-2.5">
                {recent!.map((request) => (
                  <MarketRequestCard
                    key={request.id}
                    request={request}
                    onClick={() => onOpenRequest(request.id)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Коротко о процессе */}
          <section className="rounded-2xl border bg-card/60 p-4">
            <h2 className="text-sm font-semibold">{t('market.how')}</h2>
            <ol className="mt-3 space-y-3">
              {[
                t('market.how1'),
                t('market.how2'),
                t('market.how3'),
              ].map((text, index) => (
                <li key={text} className="flex gap-3 text-xs leading-5 text-muted-foreground">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                    {index + 1}
                  </span>
                  <span>{text}</span>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </div>
    </div>
  );
}
